/**
 * Visual config per {@link ConceptMasteryStatus} (mindmap standalone spec).
 * Session pills use {@link navPillConfig}; they do not change node fill color.
 */

import type { SessionRecommendationNextActionResponse } from '../api/learners';
import type {
  ConceptMasteryStatus,
  ConceptNavHighlight,
  LayoutNode,
} from './knowledgeConcepts.types';

/** Full styling + panel copy + CTA for each mastery status. */
export const masteryStatusConfig: Record<
  ConceptMasteryStatus,
  {
    circleBg: string;
    borderColor: string;
    shadow: string;
    labelBg: string;
    labelBorder: string;
    labelColor: string;
    labelFontWeight: number;
    badgeBg: string;
    badgeColor: string;
    badgeText: string;
    /** i18n key for {@link badgeText}. */
    badgeTextKey: string;
    badgeEmoji: string;
    btnBg: string;
    btnColor: string;
    btnShadow: string;
    btnLabel: string;
    /** i18n key for {@link btnLabel}. */
    btnLabelKey: string;
    panelDesc: string;
    /** i18n key for {@link panelDesc}. */
    panelDescKey: string;
    illustrationBg: string;
  }
> = {
  mastered: {
    circleBg: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    borderColor: '#10b981',
    shadow: '0 4px 12px rgba(4, 120, 87, 0.2), 0 1px 3px rgba(4, 120, 87, 0.1)',
    labelBg: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    labelBorder: '#10b981',
    labelColor: '#064e3b',
    labelFontWeight: 600,
    badgeBg: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
    badgeColor: 'white',
    badgeText: 'Mastered',
    badgeTextKey: 'PAL_KC_STATUS_MASTERED',
    btnBg: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
    btnColor: 'white',
    btnShadow: '0 6px 24px rgba(4,120,87,0.35)',
    btnLabel: 'Review Again',
    btnLabelKey: 'PAL_KC_BTN_REVIEW_AGAIN',
    panelDesc:
      "Great work! You've mastered this concept. You can review it again to reinforce your understanding.",
    panelDescKey: 'PAL_KC_DESC_MASTERED',
    illustrationBg:
      'linear-gradient(180deg, rgba(4,120,87,0.08) 0%, rgba(4,120,87,0.02) 100%)',
    badgeEmoji: '✓',
  },
  confident: {
    circleBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderColor: '#4ade80',
    shadow:
      '0 4px 12px rgba(34, 197, 94, 0.15), 0 1px 3px rgba(34, 197, 94, 0.08)',
    labelBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    labelBorder: '#4ade80',
    labelColor: '#166534',
    labelFontWeight: 600,
    badgeBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    badgeColor: 'white',
    badgeText: 'Confident',
    badgeTextKey: 'PAL_KC_STATUS_CONFIDENT',
    btnBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    btnColor: 'white',
    btnShadow: '0 6px 24px rgba(34,197,94,0.35)',
    btnLabel: 'Start Practising',
    btnLabelKey: 'PAL_KC_BTN_START_PRACTISING',
    panelDesc:
      'You have a solid foundation here. A quick practice session will help reinforce what you already know.',
    panelDescKey: 'PAL_KC_DESC_CONFIDENT',
    illustrationBg:
      'linear-gradient(180deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)',
    badgeEmoji: '★',
  },
  developing: {
    circleBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    borderColor: '#fbbf24',
    shadow:
      '0 4px 14px rgba(245, 158, 11, 0.15), 0 1px 3px rgba(245, 158, 11, 0.08)',
    labelBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    labelBorder: '#fbbf24',
    labelColor: '#92400e',
    labelFontWeight: 600,
    badgeBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    badgeColor: '#0a0a0f',
    badgeText: 'Developing',
    badgeTextKey: 'PAL_KC_STATUS_DEVELOPING',
    btnBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    btnColor: '#0a0a0f',
    btnShadow: '0 6px 24px rgba(245,158,11,0.35)',
    btnLabel: 'Start Practising',
    btnLabelKey: 'PAL_KC_BTN_START_PRACTISING',
    panelDesc:
      'You showed partial understanding here. Some focused practice will help solidify your knowledge.',
    panelDescKey: 'PAL_KC_DESC_DEVELOPING',
    illustrationBg:
      'linear-gradient(180deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)',
    badgeEmoji: '~',
  },
  beginning: {
    circleBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    borderColor: '#fbbf24',
    shadow:
      '0 4px 14px rgba(245, 158, 11, 0.15), 0 1px 3px rgba(245, 158, 11, 0.08)',
    labelBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    labelBorder: '#fbbf24',
    labelColor: '#92400e',
    labelFontWeight: 600,
    badgeBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    badgeColor: '#0a0a0f',
    badgeText: 'Developing',
    badgeTextKey: 'PAL_KC_STATUS_DEVELOPING',
    btnBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    btnColor: '#0a0a0f',
    btnShadow: '0 6px 24px rgba(245,158,11,0.35)',
    btnLabel: 'Start Practising',
    btnLabelKey: 'PAL_KC_BTN_START_PRACTISING',
    panelDesc:
      'You showed partial understanding here. Some focused practice will help solidify your knowledge.',
    panelDescKey: 'PAL_KC_DESC_DEVELOPING',
    illustrationBg:
      'linear-gradient(180deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)',
    badgeEmoji: '~',
  },
  not_assessed: {
    circleBg: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
    borderColor: '#38bdf8',
    shadow:
      '0 4px 14px rgba(14, 165, 233, 0.2), 0 2px 6px rgba(14, 165, 233, 0.12)',
    labelBg: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
    labelBorder: '#38bdf8',
    labelColor: '#0c4a6e',
    labelFontWeight: 600,
    badgeBg: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    badgeColor: 'white',
    badgeText: 'Not assessed',
    badgeTextKey: 'PAL_KC_STATUS_NOT_ASSESSED',
    btnBg: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    btnColor: 'white',
    btnShadow: '0 6px 24px rgba(14,165,233,0.35)',
    btnLabel: 'Start Practising',
    btnLabelKey: 'PAL_KC_BTN_START_PRACTISING',
    panelDesc:
      "This concept hasn't been assessed yet. You'll discover your level as you learn.",
    panelDescKey: 'PAL_KC_DESC_NOT_ASSESSED',
    illustrationBg:
      'linear-gradient(180deg, rgba(14,165,233,0.08) 0%, rgba(14,165,233,0.02) 100%)',
    badgeEmoji: '?',
  },
  struggling: {
    circleBg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    borderColor: '#ef4444',
    shadow:
      '0 4px 16px rgba(239, 68, 68, 0.2), 0 2px 6px rgba(239, 68, 68, 0.1)',
    labelBg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    labelBorder: '#ef4444',
    labelColor: '#991b1b',
    labelFontWeight: 700,
    badgeBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    badgeColor: 'white',
    badgeText: 'Struggling',
    badgeTextKey: 'PAL_KC_STATUS_STRUGGLING',
    btnBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    btnColor: 'white',
    btnShadow: '0 6px 24px rgba(239,68,68,0.35)',
    btnLabel: 'Start Practising',
    btnLabelKey: 'PAL_KC_BTN_START_PRACTISING',
    panelDesc:
      "You're finding this one challenging — that's okay. Let's work through it together to build your understanding.",
    panelDescKey: 'PAL_KC_DESC_STRUGGLING',
    illustrationBg:
      'linear-gradient(180deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.02) 100%)',
    badgeEmoji: '!',
  },
};

/** Sequence index badge on the node (matches mindmap key colors). */
export const masterySequenceBadgeColors: Record<ConceptMasteryStatus, string> =
  {
    mastered: '#047857',
    confident: '#22c55e',
    developing: '#f59e0b',
    beginning: '#f59e0b',
    not_assessed: '#0284c7',
    struggling: '#ef4444',
  };

export type MasteryStatusConfigEntry =
  (typeof masteryStatusConfig)[ConceptMasteryStatus];

/**
 * i18n key for the pill above a node / panel — driven by session
 * `navigation.decision` plus {@link ConceptNavHighlight}. Callers should pass
 * the returned key through `t()`.
 */
export function getKnowledgeConceptBadgeLabel(
  node: LayoutNode,
  seqIndex: number,
  session: SessionRecommendationNextActionResponse | undefined
): string {
  const d = session?.navigation?.decision;
  const h = node.navHighlight;
  const isDiagnostic = session?.recommend_diagnostic;

  if (h === 'recommended') {
    if (d === 'not_applicable') {
      return seqIndex === 0 && !isDiagnostic
        ? 'PAL_KC_BADGE_BEGIN_HERE'
        : 'PAL_KC_BADGE_RECOMMENDED';
    }
    if (d === 'promote' || d === 'return_from_remediation') {
      return 'PAL_KC_BADGE_UP_NEXT';
    }
    if (d === 'stay' || d === 'challenge') {
      return 'PAL_KC_BADGE_CONTINUE_HERE';
    }
    return 'PAL_KC_BADGE_RECOMMENDED';
  }
  if (h === 'current') return 'PAL_KC_BADGE_JUST_MASTERED';
  if (h === 'struggling') return 'PAL_KC_BADGE_NEED_REVIEW';
  if (h === 'remediation-target') return 'PAL_KC_BADGE_REVISE_THIS';
  return masteryStatusConfig[node.conceptStatus].badgeTextKey;
}

export function shouldShowConceptStatusBadge(
  node: LayoutNode | undefined
): boolean {
  return Boolean(node?.navHighlight);
}
